import adserver from 'services/adserver';
import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from '@tanstack/react-query';
import { productsKeys } from './products.keys';
import { ProductParams, ProductTimedValueParams } from './products.types';
import {
  normalizeApiRequest,
  normalizeApiResponse,
  prepareTimedSegmentsPayloadForApi,
} from './products.helpers';

const createProduct = async (data: ProductParams): Promise<ProductParams> => {
  const payload = prepareTimedSegmentsPayloadForApi(data);
  const { data: response } = await adserver({
    url: '/product/',
    method: 'POST',
    data: normalizeApiRequest(payload),
    headers: {},
  });
  return normalizeApiResponse(response);
};

const updateProduct = async (data: ProductParams): Promise<ProductParams> => {
  const payload = prepareTimedSegmentsPayloadForApi(data);
  const { data: response } = await adserver({
    url: `/product/${data.id}`,
    method: 'PATCH',
    data: normalizeApiRequest(payload),
    headers: {},
  });
  return normalizeApiResponse(response);
};

const deleteProduct = async (productId: number): Promise<ProductParams> => {
  const { data: response } = await adserver({
    url: `/product/${productId}`,
    method: 'DELETE',
    headers: {},
  });
  return normalizeApiResponse(response);
};

const toggleStatus = async (product: ProductParams): Promise<ProductParams> => {
  const { id } = product;
  const isActive = !product?.isActive;
  const reqData = {
    isActive,
  };
  const { data: response } = await adserver({
    url: `/product/${id}`,
    method: 'PATCH',
    data: reqData,
    headers: {},
  });
  return normalizeApiResponse(response);
};

const clone = async (productId: number): Promise<ProductParams> => {
  const { data: product } = await adserver({
    url: `/product/${productId}`,
    method: 'GET',
    headers: {},
  });

  if (!product) {
    throw new Error('Product not found');
  }

  const timeSegments = product.timedSegments.map(
    (segment: ProductTimedValueParams) => {
      const { id, ...rest } = segment;
      return rest;
    },
  );

  const clonedProductObj = {
    ...product,
    generalValue: {
      ...product.generalValue,
      name: `${
        product.generalValue.name
      } (copy ${new Date().toLocaleString()})`,
    },
    timedSegments: timeSegments,
  };

  const payload = prepareTimedSegmentsPayloadForApi(clonedProductObj);
  const { data: response } = await adserver({
    url: '/product/',
    method: 'POST',
    data: normalizeApiRequest(payload),
    headers: {},
  });
  return normalizeApiResponse(response);
};

export const useCreateProduct = (): UseMutationResult<
  ProductParams,
  unknown,
  ProductParams,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProduct,
    onSuccess: data => {
      queryClient.setQueryData(productsKeys.detail(data.id), data);
      queryClient.invalidateQueries({
        queryKey: productsKeys.lists(),
      });
    },
  });
};

export const useUpdateProduct = (): UseMutationResult<
  ProductParams,
  unknown,
  ProductParams,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProduct,
    onSuccess: data => {
      queryClient.setQueryData(productsKeys.detail(data.id), data);
      queryClient.invalidateQueries({
        queryKey: productsKeys.lists(),
      });
    },
  });
};

export const useDeleteProduct = (): UseMutationResult<
  ProductParams,
  unknown,
  { productId: number },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId }: { productId: number }) =>
      deleteProduct(productId),
    onSuccess: data => {
      queryClient.removeQueries(productsKeys.detail(data.id));
      queryClient.invalidateQueries({
        queryKey: productsKeys.lists(),
      });
    },
  });
};

export const useCloneProduct = (): UseMutationResult<
  ProductParams,
  unknown,
  number,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clone,
    onSuccess: data => {
      queryClient.setQueryData(productsKeys.detail(data.id), data);
      queryClient.invalidateQueries({
        queryKey: productsKeys.lists(),
      });
    },
  });
};

export const useToggleStatus = (): UseMutationResult<
  ProductParams,
  unknown,
  ProductParams,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toggleStatus,
    onSuccess: data => {
      queryClient.setQueryData(productsKeys.detail(data.id), data);
      queryClient.invalidateQueries({
        queryKey: productsKeys.lists(),
      });
    },
  });
};
